import { Alert, AlertAction, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type HubContextInfo, useAgentChat } from '@/apps/ai/hooks/useAgentChat';
import { TicketUpdatesSection } from '@/apps/support/components/TicketUpdatesSection';
import { useInvalidateSupport } from '@/apps/support/hooks/useSupportQueries';
import { type AIChatOpenSource, useAIChat } from '@/contexts/AIChatContext';
import { useHub } from '@/contexts/HubContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getAgentConfig, type StarterPrompt } from '../agent-registry';
import { ChatInput } from './ChatInput';
import { type ChatMessage, ChatMessages } from './ChatMessages';
import { operatorCountryName } from './operatorCountry';
import { useFileAttachments } from './useFileAttachments';
import { WelcomeScreen } from './WelcomeScreen';

/** Dispatched when an issue is created so the support list can refresh. */
export const SUPPORT_TICKET_CREATED_EVENT = 'support:ticket-created';

interface AgentIssueChatProps {
  /** Agent-registry source — drives the welcome screen / badge config. */
  source: AIChatOpenSource;
  /** AgentOS agent id to converse with. */
  agentId: string;
  /** Input placeholder text. */
  placeholder: string;
  /** Optional starter-prompt router: return a source to switch agents, or null to send as a message. */
  routePrompt?: (prompt: StarterPrompt) => AIChatOpenSource | null;
}

/** Parse "Created issue #123: https://..." from create_github_issue tool result */
function parseIssueFromToolResult(result: string | undefined): { number: number } | null {
  if (!result || typeof result !== 'string') return null;
  const match = result.match(/Created issue #(\d+)/);
  if (!match) return null;
  return { number: parseInt(match[1], 10) };
}

/**
 * Shared chat surface for the issue-filing support agent (bugs and needs).
 * Owns the agent wiring, Hub context, issue-creation tracking, and render tree so both
 * flows stay in sync — parameterised by agent id, placeholder, and starter-prompt routing.
 */
export function AgentIssueChat({ source, agentId, placeholder, routePrompt }: AgentIssueChatProps) {
  const { messages: agentMessages, streaming, error, sendMessage, clearError } = useAgentChat(agentId, 'agent');
  const { selectedOperator, selectedClient } = useHub();
  const { user: currentUser } = useCurrentUser();
  const { open: openAIChat } = useAIChat();

  const { clearAttachments, getImageUrls, uploading, attachmentPreview, paperclipButton } = useFileAttachments({
    disabled: streaming?.active,
  });

  const userName = currentUser ? [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') || null : null;

  const hubContext = useMemo<HubContextInfo>(
    () => ({
      format: 'hub',
      operatorId: selectedOperator?.id ?? null,
      operatorName: selectedOperator?.name ?? null,
      operatorCountry: operatorCountryName(selectedOperator?.headquarters_country),
      clientId: selectedClient?.id ?? null,
      clientName: selectedClient?.display_name ?? null,
      userName,
      userEmail: currentUser?.email ?? null,
    }),
    [
      selectedOperator?.id,
      selectedOperator?.name,
      selectedOperator?.headquarters_country,
      selectedClient?.id,
      selectedClient?.display_name,
      userName,
      currentUser?.email,
    ]
  );

  // Track issue creation from tool calls
  const issueNumber = useMemo(() => {
    for (let i = agentMessages.length - 1; i >= 0; i--) {
      const msg = agentMessages[i];
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          if (tc.tool_name === 'create_github_issue') {
            const parsed = parseIssueFromToolResult(tc.result);
            if (parsed) return parsed.number;
          }
        }
      }
    }
    return null;
  }, [agentMessages]);

  const invalidateSupport = useInvalidateSupport();
  const lastNotifiedRef = useRef<number | null>(null);
  useEffect(() => {
    if (issueNumber !== null && issueNumber !== lastNotifiedRef.current) {
      lastNotifiedRef.current = issueNumber;
      invalidateSupport();
      window.dispatchEvent(new CustomEvent(SUPPORT_TICKET_CREATED_EVENT));
    }
  }, [issueNumber, invalidateSupport]);

  const handleSend = useCallback(
    (text: string) => {
      const imageUrls = getImageUrls();
      let messageText = text;
      if (imageUrls.length > 0) {
        const attachmentSection = imageUrls.map((u, i) => `![Screenshot ${i + 1}](${u})`).join('\n');
        messageText = `${text}\n\nAttached screenshots:\n${attachmentSection}`;
      }
      sendMessage(messageText, hubContext);
      clearAttachments();
    },
    [hubContext, sendMessage, getImageUrls, clearAttachments]
  );

  const handleChoosePrompt = useCallback(
    (prompt: StarterPrompt) => {
      const routedSource = routePrompt?.(prompt) ?? null;
      if (routedSource) {
        openAIChat({ source: routedSource });
      } else {
        handleSend(prompt.text);
      }
    },
    [routePrompt, openAIChat, handleSend]
  );

  // Map agent messages to shared ChatMessage format
  const messages = useMemo<ChatMessage[]>(
    () =>
      agentMessages.map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        toolCalls: m.toolCalls,
      })),
    [agentMessages]
  );

  const agentConfig = getAgentConfig(source);

  return (
    <>
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-hidden">
          <ChatMessages
            messages={messages}
            streaming={streaming}
            agentConfig={agentConfig}
            onSuggestion={streaming?.active ? undefined : handleSend}
            emptyState={<WelcomeScreen agentConfig={agentConfig} onChoosePrompt={handleChoosePrompt} disabled={streaming?.active} />}
          />
        </div>
        {issueNumber !== null && (
          <div className="shrink-0 px-2 pb-2">
            <TicketUpdatesSection issueNumber={issueNumber} />
          </div>
        )}
      </div>
      <div className="shrink-0 stack gap-2">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            <AlertAction>
              <Button variant="destructive" size="sm" onClick={clearError}>
                Dismiss
              </Button>
            </AlertAction>
          </Alert>
        )}
        <ChatInput
          onSend={handleSend}
          disabled={streaming?.active || uploading}
          placeholder={placeholder}
          headerContent={attachmentPreview}
          extraActions={paperclipButton}
        />
      </div>
    </>
  );
}
